import { Link } from "react-router-dom";
import { getSupplierName } from "../../../../data/flight/supplier/getSupplierName";
import {
  formatChangeDate,
  getFlightBookingFromChange,
  getRequestedDateChangeLines,
  getChangeRequestPassengers,
  isRouteChangeRequest,
  getScopedItineraryLegs,
} from "../../../../data/flight/order/getRequestedDateChange";
import { getChangeKindLabel } from "../../../../data/flight/order/getChangeAdminGuidance";

function formatDate(raw) {
  return formatChangeDate(raw) || "-";
}

function getBooking(data) {
  return getFlightBookingFromChange(data);
}

function personName(person) {
  return [person?.firstName, person?.middleName, person?.lastName]
    .filter(Boolean)
    .join(" ");
}

function getTicketNumbers(travelers) {
  return [
    ...new Set(
      (travelers || []).flatMap((t) => t?.ticketNumbers || []).filter(Boolean),
    ),
  ];
}

function getAirlinePnrs(booking) {
  const fromTravelers = (booking?.travelers || [])
    .flatMap((t) => t?.pnrList || [])
    .map((pnr) => pnr?.airlinePnr)
    .filter(Boolean);
  const unique = [...new Set(fromTravelers.map((v) => String(v).toUpperCase()))];
  return unique;
}

function groupFlightsBySegment(flights) {
  const groups = [];
  for (const flight of flights || []) {
    const key = String(flight?.segment ?? groups.length + 1);
    let group = groups.find((item) => item.key === key);
    if (!group) {
      group = { key, flights: [] };
      groups.push(group);
    }
    group.flights.push(flight);
  }
  return groups;
}

function Row({ label, children, note }) {
  if (!children && !note) return null;
  return (
    <div className="grid grid-cols-[120px_1fr] sm:grid-cols-[150px_1fr] gap-2 py-2.5 border-b border-gray-100 last:border-b-0">
      <div className="text-xs text-gray-500 pt-0.5">{label}</div>
      <div className="min-w-0 flex flex-col gap-0.5">
        <div className="text-sm text-gray-900 break-words">{children}</div>
        {note ? <div className="text-xs text-gray-500">{note}</div> : null}
      </div>
    </div>
  );
}

function OriginalItinerary({ booking, data }) {
  const scoped = data ? getScopedItineraryLegs(data) : null;
  const flights =
    scoped?.legs?.length > 0 ? scoped.legs : booking?.flights || [];
  const groups = groupFlightsBySegment(flights);
  const totalDirections = new Set(
    (scoped?.allLegs || booking?.flights || [])
      .map((leg) => String(leg?.segment))
      .filter(Boolean),
  ).size;
  if (!groups.length) {
    const destinations = booking?.destinations || [];
    if (!destinations.length) {
      return <span className="text-amber-700">No original itinerary listed.</span>;
    }
    return (
      <div className="flex flex-col gap-2">
        {destinations.map((dest, index) => (
          <div
            key={`${dest?.from}-${dest?.to}-${index}`}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2"
          >
            <div className="text-sm font-semibold text-gray-900">
              {[dest?.from, dest?.to].filter(Boolean).join(" → ") || `Leg ${index + 1}`}
            </div>
            <div className="text-xs text-gray-600 mt-0.5">
              {formatDate(dest?.departureDate)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {scoped?.partial ? (
        <div className="text-xs text-amber-800">
          Showing {groups.length} of {totalDirections} directions in this
          request — other flights stay as booked.
        </div>
      ) : null}
      {groups.map((group, index) => {
        const first = group.flights[0];
        const last = group.flights[group.flights.length - 1];
        const route = [
          first?.departureLocation || first?.departure?.location,
          last?.arrivalLocation || last?.arrival?.location,
        ]
          .filter(Boolean)
          .join(" → ");
        const cities = [
          first?.departureLocationName,
          last?.arrivalLocationName,
        ]
          .filter(Boolean)
          .join(" to ");
        const airline = [
          first?.airlineName || first?.airline,
          first?.flightNumber,
        ]
          .filter(Boolean)
          .join(" ");
        const times = [first?.departureTime, last?.arrivalTime]
          .filter(Boolean)
          .join(" – ");
        const labelCount = totalDirections > 1 ? totalDirections : groups.length;

        return (
          <div
            key={group.key}
            className="rounded-lg border border-gray-200 bg-white px-3 py-2"
          >
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {labelCount > 1
                ? group.key === "1"
                  ? "Outbound"
                  : group.key === "2"
                    ? "Return"
                    : index === 0
                      ? "Outbound"
                      : index === 1
                        ? "Return"
                        : `Segment ${group.key}`
                : "Itinerary"}
            </div>
            <div className="text-sm font-semibold text-gray-900 mt-0.5">
              {route || "Flight"}
            </div>
            {cities ? <div className="text-xs text-gray-500">{cities}</div> : null}
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600">
              {airline ? <span>{airline}</span> : null}
              {first?.departureDate ? (
                <span>{formatDate(first.departureDate)}</span>
              ) : null}
              {times ? <span>{times}</span> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RequestedChanges({ data, booking }) {
  const lines = getRequestedDateChangeLines(data, booking);
  const routeChange = isRouteChangeRequest(data, booking);
  const changeRefs = new Set(
    (data?.directionsToChange || []).map((ref) => String(ref)),
  );
  const flights = booking?.flights || [];

  if (!lines.length && !changeRefs.size) {
    return (
      <span className="text-amber-700">
        No requested dates or segments listed.
      </span>
    );
  }

  if (!lines.length) {
    const changedFlights = flights.filter((flight) =>
      changeRefs.has(String(flight?.segmentRef)),
    );
    return (
      <div className="flex flex-col gap-1">
        {changedFlights.map((flight) => (
          <div key={flight?.segmentRef || flight?._id} className="text-sm">
            {[flight?.departureLocation, flight?.arrivalLocation]
              .filter(Boolean)
              .join(" → ")}{" "}
            · segment {flight?.segmentRef || "-"}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {routeChange ? (
        <span className="inline-flex w-fit rounded-md bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
          Includes a route change
        </span>
      ) : null}
      {lines.map((item) => (
        <div
          key={`${item.segmentRef || item.route}-${item.newDate}-${item.originalRoute || ""}`}
          className="rounded-lg border border-primary1/20 bg-primary1/5 px-3 py-2"
        >
          {item.routeChanged && item.originalRoute ? (
            <>
              <div className="mb-1">
                <span className="inline-flex rounded-md bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
                  Route change
                </span>
              </div>
              <div className="text-xs text-gray-600">Was {item.originalRoute}</div>
              <div className="text-sm font-semibold text-gray-900">
                Now {item.route}
                {item.stops?.length ? (
                  <span className="font-normal text-gray-600">
                    {" "}
                    via {item.stops.join(", ")}
                  </span>
                ) : null}
              </div>
            </>
          ) : (
            <div className="text-sm font-semibold text-gray-900">
              {item.route}
              {item.stops?.length ? (
                <span className="font-normal text-gray-600">
                  {" "}
                  via {item.stops.join(", ")}
                </span>
              ) : null}
            </div>
          )}
          <div className="mt-1 text-sm text-gray-800">
            {item.oldLabel} →{" "}
            <span className="font-semibold text-primary1">{item.newLabel}</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600">
            {item.segment ? <span>Segment {item.segment}</span> : null}
            {item.journeyType ? <span>{item.journeyType}</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function ChangeDateConfirmationRequestDetails({ data }) {
  if (!data) return null;

  const booking = getBooking(data) || {};
  const travelers = booking?.travelers || [];
  const passengerInfo = Array.isArray(data?.passengerInfo)
    ? data.passengerInfo
    : [];
  const infoIds = passengerInfo.map((p) => String(p?.id)).filter(Boolean);
  const passengerIds = (data?.passengerIds || []).map(String).filter(Boolean);
  const scopeIds = infoIds.length ? infoIds : passengerIds;
  const selectedTravelers = scopeIds.length
    ? travelers.filter((t) => scopeIds.includes(String(t?.id)))
    : travelers;
  const changeScoped = Boolean(scopeIds.length);
  const tickets = getTicketNumbers(
    changeScoped ? selectedTravelers : travelers,
  );
  const airlinePnrs = getAirlinePnrs(booking);
  const airline =
    booking?.flights?.[0]?.airlineName ||
    booking?.flights?.[0]?.airline ||
    booking?.validatingAirline ||
    "-";
  const supplierCode = booking?.supplier || data?.supplier;
  const supplier = getSupplierName(supplierCode) || supplierCode || "-";
  const segmentRefs = [
    ...new Set(
      [
        ...(data?.directionsToChange || []),
        ...(data?.newDirections || []).map((d) => d?.segmentRef),
      ]
        .filter(Boolean)
        .map(String),
    ),
  ];

  const passengerNames = getChangeRequestPassengers(data, booking);
  const passengerLine = passengerNames.length
    ? passengerNames.join(", ")
    : "No passengers listed";
  const kindLabel = getChangeKindLabel(data);
  const routeChange = isRouteChangeRequest(data, booking);

  return (
    <div className="w-full rounded-xl border border-gray-200 bg-white mt-4 px-4 py-4">
      <div className="mb-3">
        <h3 className="text-base font-semibold text-gray-900 capitalize">
          {kindLabel} request details
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {routeChange
            ? "What was booked, who is changing, and the new route requested."
            : "What was booked, who is changing, and what was requested."}
        </p>
      </div>

      <div className="rounded-lg border border-gray-100 bg-[#F8FAFC] px-3">
        <Row label="Original itinerary">
          <OriginalItinerary booking={booking} data={data} />
        </Row>

        <Row
          label="Ticket"
          note={
            tickets.length
              ? null
              : "No ticket number found. Confirm this booking was issued."
          }
        >
          {tickets.length ? tickets.join(" | ") : booking?.status || "-"}
        </Row>

        <Row
          label="Passengers"
          note={
            changeScoped
              ? `${passengerNames.length || scopeIds.length} passenger${
                  (passengerNames.length || scopeIds.length) === 1 ? "" : "s"
                } on this change`
              : travelers.length
                ? "All passengers on the ticket"
                : null
          }
        >
          {passengerLine}
        </Row>

        <Row label="Segments changing">
          {segmentRefs.length ? (
            <div className="flex flex-wrap gap-1.5">
              {segmentRefs.map((ref) => (
                <span
                  key={ref}
                  className="inline-flex rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-700"
                >
                  {ref}
                </span>
              ))}
            </div>
          ) : (
            "No segment refs listed"
          )}
        </Row>

        <Row label={routeChange ? "Requested route" : "Requested new dates"}>
          <RequestedChanges data={data} booking={booking} />
        </Row>

        <Row label="Airline">{airline}</Row>
        <Row label="Supplier">{supplier}</Row>

        <Row label="References">
          <div className="flex flex-col gap-1 text-sm">
            {booking?.bookingId ? (
              <div>
                Booking{" "}
                <Link
                  to={`/order/flight/ticket/detail/${booking.bookingId}?page=Bookings`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary1 hover:underline"
                >
                  {booking.bookingId}
                </Link>
              </div>
            ) : null}
            {data?.orderId ? <div>Change order {data.orderId}</div> : null}
            {booking?.pnr ? <div>GDS PNR {booking.pnr}</div> : null}
            {airlinePnrs.length ? (
              <div>Airline PNR {airlinePnrs.join(" | ")}</div>
            ) : null}
            {booking?.orderId && booking.orderId !== data?.orderId ? (
              <div>Flight order {booking.orderId}</div>
            ) : null}
            {!booking?.bookingId &&
            !data?.orderId &&
            !booking?.pnr &&
            !airlinePnrs.length ? (
              <span>-</span>
            ) : null}
          </div>
        </Row>

        {data?.reason ? (
          <Row label="Reason" note={data?.remark || null}>
            {data.reason}
          </Row>
        ) : null}
      </div>
    </div>
  );
}
